import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { CONTACT, LOGO_MARK } from "@/lib/constants";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed. Welcome to the Journal.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer className="bg-black border-t border-white/10" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-6">
              [ Have a project in mind? ]
            </p>
            <Link to="/contact" data-testid="footer-cta" className="group block">
              <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-[16vw] lg:text-[11vw]">
                <span className="block group-hover:translate-x-4 transition-transform duration-500">Let's</span>
                <span className="block text-white/20 group-hover:text-white transition-colors duration-500">
                  Talk<span className="text-[color:var(--accent)]">.</span>
                </span>
              </h2>
            </Link>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 border border-white/20 px-8 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors self-start"
            data-testid="footer-start-project"
          >
            Start a project <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mt-24 md:mt-32 border-t border-white/10 pt-14">
          <div className="md:col-span-1">
            <img src={LOGO_MARK} alt="LIS" className="logo-invert h-14 w-auto mb-5" />
            <p className="font-mono text-sm text-white/50 leading-relaxed">
              Liinex Information Systems — engineering software, web & AI for ambitious teams.
            </p>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-5">Navigate</p>
            <ul className="space-y-3 font-mono text-sm">
              <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/blog" className="text-white/70 hover:text-white transition-colors">Journal</Link></li>
              <li><Link to="/careers" className="text-white/70 hover:text-white transition-colors">Careers</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-5">Reach us</p>
            <ul className="space-y-3 font-mono text-sm">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="text-white/70 hover:text-[color:var(--accent)] transition-colors" data-testid="footer-email">
                  {CONTACT.email}
                </a>
              </li>
              {CONTACT.phonesRaw.map((raw, i) => (
                <li key={raw}>
                  <a href={`tel:${raw}`} className="text-white/70 hover:text-white transition-colors" data-testid={`footer-phone-${i}`}>
                    {CONTACT.phones[i]}
                  </a>
                </li>
              ))}
              <li className="text-white/50 pt-2 leading-relaxed">
                {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
              </li>
            </ul>
          </div>

          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-5">The Journal</p>
            <p className="font-mono text-sm text-white/50 mb-4">Ideas on web, software & AI. No noise.</p>
            <form onSubmit={subscribe} className="flex items-center border-b border-white/20 focus-within:border-white transition-colors">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-transparent flex-1 py-3 font-mono text-sm placeholder-white/30 focus:outline-none"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                disabled={busy}
                className="text-white/70 hover:text-[color:var(--accent)] transition-colors disabled:opacity-40"
                data-testid="newsletter-submit"
                aria-label="Subscribe"
              >
                <ArrowUpRight size={18} />
              </button>
            </form>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-4 mt-16 pt-8 border-t border-white/10 font-mono text-xs text-white/40 uppercase tracking-[0.15em]">
          <span>© {year} Liinex Information Systems ®</span>
          <span>Ghaziabad · India</span>
        </div>
      </div>
    </footer>
  );
}
